import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  IconButton,
  Switch,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

type Role = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
const ROLES: Role[] = ['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'];

/**
 * What each role means inside this organization. None of them is a platform
 * role: super-admin (the plan catalogue, cross-tenant console, deployment and
 * AI configuration) is granted separately by the platform operator and cannot
 * be handed out from this screen.
 */


interface Member {
  userId: string;
  email: string;
  fullName?: string;
  role: Role;
  status: 'ACTIVE' | 'DISABLED' | 'INVITED';
  source: string;
  lastLoginAt?: string;
  tester: boolean;
  remoteControl: boolean;
}

function apiError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string; details?: { path: string; message: string }[] } } } };
  const d = e.response?.data?.error;
  return d?.details?.map((x) => `${x.path}: ${x.message}`).join(' · ') || d?.message || fallback;
}


export function MembersPage() {
  const t = useT().members;
  const { data: me } = useMe();
  const org = me?.organizations[0];
  const orgId = org?.id;
  const myRole = org?.role;
  const canManage = myRole === 'OWNER' || myRole === 'ADMIN';
  const qc = useQueryClient();
  const base = `/v1/orgs/${orgId}/members`;

  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // The plan decides whether tester mode exists and how many people may hold
  // it, so the switch has to know before it lets an admin try.
  const entitlements = useQuery({
    enabled: !!orgId && canManage,
    queryKey: ['entitlements', orgId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/billing/entitlements`)).data as {
        name: string;
        testerEnabled: boolean;
        testerSeats: number;
      },
  });

  const members = useQuery({
    enabled: !!orgId && canManage,
    queryKey: ['members', orgId],
    queryFn: async () => (await api.get(base)).data.data as Member[],
  });

  const patch = useMutation({
    mutationFn: (v: { userId: string; body: Record<string, string | boolean> }) => api.patch(`${base}/${v.userId}`, v.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', orgId] });
      setMsg({ kind: 'success', text: t.saved });
    },
    onError: (e) => setMsg({ kind: 'error', text: apiError(e, t.somethingWrong) }),
  });

  const passwordLink = useMutation({
    mutationFn: async (v: { userId: string; email: string }) =>
      ({
        ...((await api.post(`${base}/${v.userId}/password-link`)).data as {
          kind: 'invitation' | 'reset';
          emailSent: boolean;
        }),
        email: v.email,
      }),
    onSuccess: (r) => {
      const what = r.kind === 'invitation' ? t.link.invitation : t.link.reset;
      setMsg(
        r.emailSent
          ? { kind: 'success', text: t.link.sent(what, r.email) }
          : { kind: 'error', text: t.link.failed(what) },
      );
      qc.invalidateQueries({ queryKey: ['members', orgId] });
    },
    onError: (e) => setMsg({ kind: 'error', text: apiError(e, t.somethingWrong) }),
  });

  if (!me) return null;
  if (!canManage) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>{t.title}</Typography>
        <Alert severity="info">{t.needAdmin}</Alert>
      </Box>
    );
  }

  const sort = useSort<'user' | 'status' | 'source' | 'lastSignIn'>('user', 'asc');
  const rows = sort.sorted(members.data ?? [], {
    user: (m) => m.fullName || m.email,
    status: (m) => m.status,
    source: (m) => m.source,
    lastSignIn: (m) => m.lastLoginAt,
  });
  const ent = entitlements.data;
  const testersUsed = rows.filter((m) => m.tester).length;
  const testerCap = ent?.testerSeats ?? 0; // 0 = unlimited once enabled
  const testerSeatsLeft = !ent?.testerEnabled ? 0 : testerCap === 0 ? Infinity : testerCap - testersUsed;
  // Flags survive a downgrade, so an org can end up over its plan's cap; say so
  // plainly rather than silently letting the count drift past the limit.
  const overCap = !!ent?.testerEnabled && testerCap > 0 && testersUsed > testerCap;
  const testerHint = !ent
    ? ''
    : !ent.testerEnabled
      ? t.tester.notIncluded(ent.name)
      : testerCap === 0
        ? t.tester.unlimited(ent.name, testersUsed)
        : overCap
          ? t.tester.overCap(ent.name, testerCap, testersUsed)
          : t.tester.inUse(ent.name, testersUsed, testerCap);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h5">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">{t.countIn(rows.length, org?.name ?? '')}</Typography>
        </Stack>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)}>
          {t.addUser}
        </Button>
      </Stack>

      {testerHint &&
        (ent && (!ent.testerEnabled || overCap) ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {testerHint}
            {!ent.testerEnabled && t.upgradeForTester}
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {testerHint}
          </Typography>
        ))}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.rolesNote}
      </Typography>

      {msg && (
        <Alert severity={msg.kind} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {sort.head('user', t.columns.user)}
              <TableCell>
                <Tooltip
                  title={
                    <Box>
                      {ROLES.map((r) => (
                        <Typography key={r} variant="caption" component="div" sx={{ mb: 0.5 }}>
                          <b>{t.roleNames[r] ?? r}:</b> {t.roleHelp[r]}
                        </Typography>
                      ))}
                    </Box>
                  }
                >
                  <span>{t.columns.role}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title={t.tester.columnHint(testerHint)}>
                  <span>{t.columns.tester}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title={t.remoteControlHint}>
                  <span>{t.columns.remoteControl}</span>
                </Tooltip>
              </TableCell>
              {sort.head('status', t.columns.status)}
              {sort.head('source', t.columns.source)}
              {sort.head('lastSignIn', t.columns.lastSignIn)}
              <TableCell align="right">{t.columns.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => {
              const isSelf = m.userId === me.id;
              const disabled = m.status === 'DISABLED';
              const pending = m.status === 'INVITED';
              return (
                <TableRow key={m.userId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.fullName || m.email}
                      {isSelf && <Chip label={t.you} size="small" sx={{ ml: 1, height: 18 }} />}
                    </Typography>
                    {m.fullName && (
                      <Typography variant="caption" color="text.secondary">{m.email}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={m.role}
                      variant="standard"
                      disableUnderline
                      disabled={isSelf || patch.isPending}
                      onChange={(e) => patch.mutate({ userId: m.userId, body: { role: e.target.value } })}
                      sx={{ fontSize: '0.85rem', minWidth: 96 }}
                    >
                      {ROLES.map((r) => (
                        <MenuItem key={r} value={r} sx={{ fontSize: '0.85rem' }}>{r}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={!m.tester && testerSeatsLeft <= 0 ? testerHint : ''}>
                      <span>
                        <Switch
                          size="small"
                          checked={m.tester}
                          // Revoking always works; granting needs a free seat on the plan.
                          disabled={patch.isPending || (!m.tester && testerSeatsLeft <= 0)}
                          onChange={(e) => patch.mutate({ userId: m.userId, body: { tester: e.target.checked } })}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={m.remoteControl}
                      disabled={patch.isPending}
                      onChange={(e) => patch.mutate({ userId: m.userId, body: { remoteControl: e.target.checked } })}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={pending ? t.pendingHint : ''}>
                      <Chip
                        size="small"
                        label={disabled ? t.statuses.disabled : pending ? t.statuses.pending : t.statuses.active}
                        color={disabled ? 'default' : pending ? 'warning' : 'success'}
                        variant={disabled || pending ? 'outlined' : 'filled'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={m.source === 'OIDC' ? t.sources.sso : t.sources.local} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {m.lastLoginAt ? formatDateTime(m.lastLoginAt) : t.never}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        m.source === 'OIDC'
                          ? t.passwordHints.sso
                          : pending
                            ? t.passwordHints.resend
                            : t.passwordHints.reset
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled || m.source === 'OIDC' || passwordLink.isPending}
                          onClick={() => passwordLink.mutate({ userId: m.userId, email: m.email })}
                        >
                          {pending ? <SendIcon fontSize="small" /> : <LockResetIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isSelf ? t.disableHints.self : disabled ? t.disableHints.enable : t.disableHints.disable}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isSelf || patch.isPending}
                          color={disabled ? 'success' : 'default'}
                          onClick={() =>
                            patch.mutate({ userId: m.userId, body: { status: disabled ? 'ACTIVE' : 'DISABLED' } })
                          }
                        >
                          {disabled ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>No users yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <AddUserDialog
        open={addOpen}
        base={base}
        onClose={() => setAddOpen(false)}
        onDone={(text) => {
          setAddOpen(false);
          setMsg({ kind: 'success', text });
          qc.invalidateQueries({ queryKey: ['members', orgId] });
        }}
        onError={(text) => setMsg({ kind: 'error', text })}
      />
    </Box>
  );
}

function AddUserDialog({
  open,
  base,
  onClose,
  onDone,
  onError,
}: {
  open: boolean;
  base: string;
  onClose: () => void;
  onDone: (text: string) => void;
  onError: (text: string) => void;
}) {
  const all = useT();
  const t = all.members.dialog;
  const roleNames = all.members.roleNames;
  const roleHelp = all.members.roleHelp;
  const common = all.common;
  const fallback = all.members.somethingWrong;
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('MEMBER');

  const reset = () => { setEmail(''); setFullName(''); setRole('MEMBER'); };

  const create = useMutation({
    mutationFn: async () =>
      (await api.post(base, { email, fullName: fullName || undefined, role })).data as {
        userId: string;
        emailSent: boolean;
        /** Why delivery failed, when it did — shown instead of a generic hint. */
        emailError?: string;
      },
    onSuccess: (data) => {
      const addr = email;
      reset();
      if (data.emailSent) {
        onDone(t.invited(addr));
      } else {
        // Saying only "could not be sent" sent us hunting for a spam folder
        // while the real cause (sandboxed mail account) was never shown.
        onDone(
          data.emailError ? t.createdNoMailWithReason(addr, data.emailError) : t.createdNoMail(addr),
        );
      }
    },
    onError: (e) => onError(apiError(e, fallback)),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.intro}
        </Typography>
        <TextField fullWidth margin="normal" label={t.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField fullWidth margin="normal" label={t.fullName} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <TextField
          select
          fullWidth
          margin="normal"
          label={t.role}
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          helperText={t.roleHelper}
        >
          {ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{roleNames[r] ?? r}</Typography>
                <Typography variant="caption" color="text.secondary">{roleHelp[r]}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{common.cancel}</Button>
        <Button
          variant="contained"
          disabled={create.isPending || !email.trim()}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t.sending : t.send}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
